import { defineStore } from "pinia";
import { ref } from "vue";
import type { ToDoList, ToDoItem } from "../types/common";
import { v4 as uuidv4 } from "uuid";

/**
 * Store для управления списками задач (todo lists)
 * Обеспечивает CRUD операции и сохранение в localStorage
 */
export const useToDoStore = defineStore("toDo", () => {
  // Основное состояние - массив списков задач
  const lists = ref<ToDoList[]>([]);

  // --- LocalStorage helpers ---

  const STORAGE_KEY = "todo-lists";

  /**
   * Сохраняет текущие списки в localStorage
   */
  function saveToLocalStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lists.value));
    } catch (e) {
      // Игнорируем ошибки localStorage (например, если он отключен)
    }
  }

  /**
   * Загружает списки из localStorage
   */
  function loadFromLocalStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        lists.value = JSON.parse(data);
      }
    } catch (e) {
      // Игнорируем ошибки localStorage
    }
  }

  // --- Store logic ---

  /**
   * Переключает статус выполнения задачи
   * @param listId - ID списка задач
   * @param itemId - ID задачи
   */
  function toggleItemComplete(listId: string, itemId: string) {
    const list = lists.value.find((l) => l.id === listId);

    if (list && Array.isArray(list.items)) {
      const item = list.items.find((i) => i.id === itemId);

      if (item) {
        item.complete = !item.complete;
      }
    }
  }

  /**
   * Удаляет задачу из списка
   * @param listId - ID списка задач
   * @param itemId - ID задачи
   */
  function removeItemFromList(listId: string, itemId: string) {
    const list = lists.value.find((l) => l.id === listId);

    if (list && Array.isArray(list.items)) {
      list.items = list.items.filter((i) => i.id !== itemId);
    }
  }

  /**
   * Удаляет весь список задач
   * @param listId - ID списка для удаления
   */
  function removeList(listId: string) {
    lists.value = lists.value.filter((l) => l.id !== listId);
    saveToLocalStorage();
  }

  /**
   * Создает новый пустой список задач
   * @returns ID созданного списка
   */
  function createNewList(id: string): ToDoList {
    const newList: ToDoList = { id, items: [] };
    lists.value.push(newList);

    return newList;
  }

  /**
   * Создает новую пустую задачу
   * @returns Новая задача с уникальным ID
   */
  function createNewTodo(): ToDoItem {
    const id = uuidv4();
    const newItem: ToDoItem = { id, text: "", complete: false };
    return newItem;
  }

  function removeEmptyItems(list?: ToDoList) {
    if (list && Array.isArray(list.items)) {
      list.items = list.items.filter((i) => i.text && i.text.trim());
    }
  }

  return {
    lists,
    toggleItemComplete,
    removeItemFromList,
    removeList,
    saveToLocalStorage,
    loadFromLocalStorage,
    createNewList,
    createNewTodo,
    removeEmptyItems,
  };
});
